import { useState, useEffect } from "react";
import axios from "axios";
import { Package, Plus, Trash2, Clock, CheckCircle, AlertCircle, FileText, RefreshCw, Edit2, Paperclip, Download, X, Upload, Search } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyYarnItem = { yarn_type: "", color: "", required_qty: "", unit: "kg" };

export default function YarnRequirementPage() {
  const [requirements, setRequirements] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [editingReq, setEditingReq] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  
  const [formData, setFormData] = useState({
    wo_id: "",
    wo_number: "",
    customer_name: "",
    required_by_date: "",
    remarks: "",
    yarn_items: [{ ...emptyYarnItem }]
  });

  useEffect(() => {
    fetchRequirements();
    fetchWorkOrders();
  }, []);

  const fetchRequirements = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/yarn-requirements`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequirements(response.data);
    } catch (err) {
      console.error("Failed to fetch yarn requirements:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/work-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkOrders(response.data);
    } catch (err) {
      console.error("Failed to fetch work orders:", err);
    }
  };

  const handleWOSelect = (woId) => {
    const wo = workOrders.find(w => w.id === woId);
    if (wo) {
      setFormData({
        ...formData,
        wo_id: wo.id,
        wo_number: wo.wo_number,
        customer_name: wo.customer_name
      });
    }
  };

  const resetForm = () => {
    setFormData({
      wo_id: "",
      wo_number: "",
      customer_name: "",
      required_by_date: "",
      remarks: "",
      yarn_items: [{ ...emptyYarnItem }]
    });
    setEditingReq(null);
  };

  const handleAddYarnItem = () => {
    if (formData.yarn_items.length < 10) {
      setFormData({
        ...formData,
        yarn_items: [...formData.yarn_items, { ...emptyYarnItem }]
      });
    }
  };

  const handleRemoveYarnItem = (index) => {
    if (formData.yarn_items.length > 1) {
      const newItems = formData.yarn_items.filter((_, i) => i !== index);
      setFormData({ ...formData, yarn_items: newItems });
    }
  };

  const handleYarnItemChange = (index, field, value) => {
    const newItems = [...formData.yarn_items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, yarn_items: newItems });
  };

  const openEditModal = (req) => {
    // Convert legacy format to new format if needed
    const yarnItems = req.yarn_items && req.yarn_items.length > 0 
      ? req.yarn_items 
      : [{ yarn_type: req.yarn_type, color: req.color || "", required_qty: req.required_qty, unit: req.unit || "kg" }];
    
    setFormData({
      wo_id: req.wo_id,
      wo_number: req.wo_number,
      customer_name: req.customer_name,
      required_by_date: req.required_by_date,
      remarks: req.remarks || "",
      yarn_items: yarnItems
    });
    setEditingReq(req);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    setSuccess("");

    try {
      const token = localStorage.getItem('token');
      
      // Convert yarn items to proper format
      const yarnItems = formData.yarn_items.map(item => ({
        yarn_type: item.yarn_type,
        color: item.color || "",
        required_qty: parseFloat(item.required_qty),
        unit: item.unit
      }));

      if (editingReq) {
        // Update existing requirement
        await axios.put(`${API}/yarn-requirements/${editingReq.id}`, {
          required_by_date: formData.required_by_date,
          remarks: formData.remarks,
          yarn_items: yarnItems
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Upload any pending files for edited requirement
        if (pendingFiles.length > 0) {
          for (const file of pendingFiles) {
            const fd = new FormData();
            fd.append('file', file);
            await axios.post(`${API}/yarn-requirements/${editingReq.id}/attachments`, fd, {
              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
              timeout: 60000
            });
          }
        }
        setSuccess("Yarn requirement updated successfully!");
      } else {
        // Create new requirement
        const res = await axios.post(`${API}/yarn-requirements`, {
          wo_id: formData.wo_id,
          wo_number: formData.wo_number,
          customer_name: formData.customer_name,
          required_by_date: formData.required_by_date,
          remarks: formData.remarks,
          yarn_items: yarnItems
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Upload pending files to the newly created requirement
        const newReqId = res.data?.id;
        if (newReqId && pendingFiles.length > 0) {
          for (const file of pendingFiles) {
            const fd = new FormData();
            fd.append('file', file);
            await axios.post(`${API}/yarn-requirements/${newReqId}/attachments`, fd, {
              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
              timeout: 60000
            });
          }
        }
        setSuccess("Yarn requirement submitted successfully!");
      }

      setShowForm(false);
      resetForm();
      setPendingFiles([]);
      fetchRequirements();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save yarn requirement");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (reqId) => {
    if (!window.confirm("Are you sure you want to delete this yarn requirement?")) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/yarn-requirements/${reqId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess("Yarn requirement deleted successfully!");
      fetchRequirements();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to delete yarn requirement");
    }
  };

  const handleFileUpload = async (reqId, file) => {
    setUploadingFile(true);
    try {
      const token = localStorage.getItem('token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      await axios.post(`${API}/yarn-requirements/${reqId}/attachments`, formDataUpload, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });
      setSuccess("File uploaded successfully!");
      fetchRequirements();
    } catch (err) {
      setError(err.response?.data?.detail || "File upload failed");
    }
    setUploadingFile(false);
  };

  const handleDownload = async (reqId, attachment) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/yarn-requirements/${reqId}/attachments/${attachment.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        timeout: 60000
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError("Download failed. Please try again.");
    }
  };

  const handleDeleteAttachment = async (reqId, attachmentId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/yarn-requirements/${reqId}/attachments/${attachmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchRequirements();
    } catch (err) {
      setError("Failed to delete attachment");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending_for_po':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300"><Clock className="w-3 h-3 mr-1" /> Pending for PO</Badge>;
      case 'po_raised':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300"><FileText className="w-3 h-3 mr-1" /> PO Raised</Badge>;
      case 'closed':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300"><CheckCircle className="w-3 h-3 mr-1" /> Closed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Helper to display yarn items summary
  const getYarnSummary = (req) => {
    const items = req.yarn_items && req.yarn_items.length > 0 ? req.yarn_items : [{ yarn_type: req.yarn_type, required_qty: req.required_qty, unit: req.unit }];
    if (items.length === 1) {
      return `${items[0].yarn_type} (${items[0].required_qty} ${items[0].unit})`;
    }
    return `${items.length} items`;
  };

  const isFormValid = () => {
    if (!editingReq && (!formData.wo_id || !formData.required_by_date)) return false;
    if (editingReq && !formData.required_by_date) return false;
    return formData.yarn_items.every(item => item.yarn_type && item.required_qty);
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center h-full">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl text-primary">Yarn Requirement</h1>
          <p className="text-muted-foreground">Submit yarn requirements for work orders (up to 10 items)</p>
        </div>
        <Button onClick={() => { resetForm(); setShowForm(true); }} data-testid="add-yarn-requirement-btn" className="min-h-[44px]">
          <Plus className="w-4 h-4 mr-2" />
          New Yarn Requirement
        </Button>
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive/30 text-destructive px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
          <Button variant="ghost" size="sm" onClick={() => setError("")} className="ml-auto">✕</Button>
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-300 text-green-800 px-4 py-3 rounded-lg flex items-center gap-2">
          <CheckCircle className="w-5 h-5" />
          {success}
          <Button variant="ghost" size="sm" onClick={() => setSuccess("")} className="ml-auto">✕</Button>
        </div>
      )}

      {/* Requirements List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            Yarn Requirements
          </CardTitle>
          <CardDescription>View and manage yarn requirements</CardDescription>
        </CardHeader>
        <CardContent>
          {requirements.length > 0 && (
            <div className="relative max-w-sm mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search by WO number, customer, yarn type..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 h-9" data-testid="yarn-search" />
            </div>
          )}
          {requirements.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No yarn requirements submitted yet</p>
              <Button variant="outline" className="mt-4 min-h-[44px]" onClick={() => { resetForm(); setShowForm(true); }}>
                Submit Your First Requirement
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>WO Number</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Yarn Items</TableHead>
                    <TableHead>Required By</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead>PO #</TableHead>
                    <TableHead>Attachments</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requirements.filter(req => {
                    if (!searchQuery) return true;
                    const q = searchQuery.toLowerCase();
                    return (req.wo_number || '').toLowerCase().includes(q) ||
                      (req.customer_name || '').toLowerCase().includes(q) ||
                      (req.yarn_items || []).some(i => (i.yarn_type || '').toLowerCase().includes(q));
                  }).map((req) => (
                    <TableRow key={req.id}>
                      <TableCell className="font-medium">{req.wo_number}</TableCell>
                      <TableCell>{req.customer_name}</TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {(req.yarn_items && req.yarn_items.length > 0 ? req.yarn_items : [{ yarn_type: req.yarn_type, color: req.color, required_qty: req.required_qty, unit: req.unit }]).map((item, idx) => (
                            <div key={idx} className="text-sm">
                              <span className="font-medium">{item.yarn_type}</span>
                              {item.color && <span className="text-muted-foreground"> ({item.color})</span>}
                              <span className="ml-2">{item.required_qty} {item.unit}</span>
                            </div>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>{req.required_by_date}</TableCell>
                      <TableCell className="text-center">{getStatusBadge(req.status)}</TableCell>
                      <TableCell>{req.po_number || '-'}</TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {/* Existing attachments */}
                          {(req.attachments || []).map((att) => (
                            <div key={att.id} className="flex items-center gap-1.5 text-xs group">
                              <Paperclip className="w-3 h-3 text-muted-foreground shrink-0" />
                              <button
                                onClick={() => handleDownload(req.id, att)}
                                className="text-blue-600 hover:underline truncate max-w-[120px] text-left"
                                title={att.original_filename}
                                data-testid={`download-att-${att.id}`}
                              >
                                {att.original_filename}
                              </button>
                              <button
                                onClick={() => handleDeleteAttachment(req.id, att.id)}
                                className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-opacity"
                                title="Remove"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                          {/* Upload button */}
                          <label className="flex items-center gap-1 text-xs text-muted-foreground hover:text-blue-600 cursor-pointer mt-1" data-testid={`upload-att-${req.id}`}>
                            <Upload className="w-3 h-3" />
                            <span>{uploadingFile ? 'Uploading...' : 'Add file'}</span>
                            <input
                              type="file"
                              className="hidden"
                              onChange={(e) => {
                                if (e.target.files[0]) handleFileUpload(req.id, e.target.files[0]);
                                e.target.value = '';
                              }}
                              disabled={uploadingFile}
                            />
                          </label>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        {req.status === 'pending_for_po' && (
                          <div className="flex justify-center gap-2">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="min-h-[44px] min-w-[44px]"
                              onClick={() => openEditModal(req)}
                              data-testid={`edit-yarn-req-${req.id}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="min-h-[44px] min-w-[44px] text-red-600 border-red-200"
                              onClick={() => handleDelete(req.id)}
                              data-testid={`delete-yarn-req-${req.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Yarn Requirement Form Modal */}
      <Dialog open={showForm} onOpenChange={(open) => { setShowForm(open); if (!open) { resetForm(); setPendingFiles([]); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingReq ? 'Edit Yarn Requirement' : 'New Yarn Requirement'}</DialogTitle>
            <DialogDescription>
              {editingReq ? 'Update yarn requirement details' : 'Submit a new yarn requirement for a work order (up to 10 items)'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Work Order Selection (only for new requirements) */}
            {!editingReq && (
              <div className="space-y-2">
                <Label>Work Order *</Label>
                <Select value={formData.wo_id} onValueChange={handleWOSelect}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Work Order" />
                  </SelectTrigger>
                  <SelectContent>
                    {workOrders.map((wo) => (
                      <SelectItem key={wo.id} value={wo.id}>
                        {wo.wo_number} - {wo.customer_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {editingReq && (
              <div className="bg-slate-50 rounded-lg p-3 mb-4">
                <div className="text-sm text-muted-foreground">Work Order</div>
                <div className="font-semibold">{formData.wo_number} - {formData.customer_name}</div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Required By Date *</Label>
              <Input
                type="date"
                value={formData.required_by_date}
                onChange={(e) => setFormData({ ...formData, required_by_date: e.target.value })}
                required
              />
            </div>

            {/* Yarn Items Section */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <Label className="text-base font-semibold">Yarn Items ({formData.yarn_items.length}/10)</Label>
                {formData.yarn_items.length < 10 && (
                  <Button type="button" variant="outline" size="sm" onClick={handleAddYarnItem} className="min-h-[40px]">
                    <Plus className="w-4 h-4 mr-1" /> Add Item
                  </Button>
                )}
              </div>
              
              <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
                {formData.yarn_items.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 p-3 bg-slate-50 rounded-lg items-end">
                    <div className="col-span-4 space-y-1">
                      <Label className="text-xs">Yarn Type *</Label>
                      <Input
                        value={item.yarn_type}
                        onChange={(e) => handleYarnItemChange(index, 'yarn_type', e.target.value)}
                        placeholder="e.g., Cotton 40s"
                        required
                      />
                    </div>
                    <div className="col-span-2 space-y-1">
                      <Label className="text-xs">Color</Label>
                      <Input
                        value={item.color}
                        onChange={(e) => handleYarnItemChange(index, 'color', e.target.value)}
                        placeholder="e.g., White"
                      />
                    </div>
                    <div className="col-span-2 space-y-1">
                      <Label className="text-xs">Qty *</Label>
                      <Input
                        type="number"
                        value={item.required_qty}
                        onChange={(e) => handleYarnItemChange(index, 'required_qty', e.target.value)}
                        placeholder="100"
                        min="0.01"
                        step="0.01"
                        required
                      />
                    </div>
                    <div className="col-span-3 space-y-1">
                      <Label className="text-xs">Unit *</Label>
                      <Select value={item.unit} onValueChange={(value) => handleYarnItemChange(index, 'unit', value)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="kg">Kilograms</SelectItem>
                          <SelectItem value="meters">Meters</SelectItem>
                          <SelectItem value="cones">Cones</SelectItem>
                          <SelectItem value="bags">Bags</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-1">
                      {formData.yarn_items.length > 1 && (
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm" 
                          className="min-h-[40px] min-w-[40px] text-red-600"
                          onClick={() => handleRemoveYarnItem(index)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Remarks</Label>
              <Textarea
                value={formData.remarks}
                onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                placeholder="Any additional notes..."
                rows={2}
              />
            </div>

            {/* File Attachment Section */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1"><Paperclip className="w-3.5 h-3.5" /> Attach Files</Label>
              <div className="p-3 border border-dashed border-muted-foreground/30 rounded-lg">
                {pendingFiles.length > 0 && (
                  <div className="space-y-1 mb-2">
                    {pendingFiles.map((file, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm bg-muted/30 rounded px-2 py-1">
                        <Paperclip className="w-3 h-3 text-muted-foreground shrink-0" />
                        <span className="truncate flex-1">{file.name}</span>
                        <span className="text-xs text-muted-foreground shrink-0">({(file.size / 1024).toFixed(0)} KB)</span>
                        <button type="button" onClick={() => setPendingFiles(pendingFiles.filter((_, i) => i !== idx))} className="text-red-500 hover:text-red-700 shrink-0">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <label className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary cursor-pointer" data-testid="attach-file-btn">
                  <Upload className="w-4 h-4" />
                  <span>Click to add files (Excel, PDF, JPG — max 10MB each)</span>
                  <input
                    type="file"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files[0]) {
                        if (e.target.files[0].size > 10 * 1024 * 1024) {
                          setError("File size must be under 10MB");
                          return;
                        }
                        setPendingFiles([...pendingFiles, e.target.files[0]]);
                      }
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
            </div>

            {/* File attachments info */}
            {editingReq && (editingReq.attachments || []).length > 0 && (
              <div className="space-y-2">
                <Label className="flex items-center gap-1"><Paperclip className="w-3.5 h-3.5" /> Attached Files</Label>
                <div className="space-y-1 p-3 bg-muted/30 rounded-lg">
                  {editingReq.attachments.map(att => (
                    <div key={att.id} className="flex items-center gap-2 text-sm">
                      <Paperclip className="w-3 h-3 text-muted-foreground" />
                      <span className="truncate">{att.original_filename}</span>
                      <span className="text-xs text-muted-foreground">({(att.size / 1024).toFixed(0)} KB)</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">Files can be managed from the table after saving</p>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowForm(false); resetForm(); setPendingFiles([]); }} className="min-h-[44px]">
                Cancel
              </Button>
              <Button type="submit" disabled={submitting || !isFormValid()} className="min-h-[44px]">
                {submitting ? 'Saving...' : (editingReq ? 'Update Requirement' : 'Submit Requirement')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
